import firebase from "../../firebase"
import {
  AnswerState,
  FeedbackState,
  TypeArray,
  InteractionType,
  FirestoreTimestampType
} from "../types"
import { reducerTypeGuard } from "../reducers/reducerGuards"
import { actionCreatorTypeGuard } from "../reducers/actionCreators"
import {
  processActionMeta,
  getArrayTypeReducerGuard
} from "../reducers"
import { AnswerStateSchema, FeedbackStateSchema } from "../schemas"

import _ from "lodash"

export type QuestionState = {
  answerStates: Array<AnswerState>
  feedbackStates: Array<FeedbackState | null>
  generalFeedbackStates: Array<FeedbackState | null>
  nonzoneFeedbackStates: Array<FeedbackState | null>
  interactionType: string
  attempts: number
  completed: boolean
  completed_timestamp: FirestoreTimestampType | null
}

const reducerAction = {
  answerStates: actionCreatorTypeGuard<QuestionState, Array<AnswerState>>(
    "answerStates",
    "QuestionState"
  ),
  feedbackStates: actionCreatorTypeGuard<
    QuestionState,
    Array<FeedbackState | null>
  >("feedbackStates", "QuestionState"),
  generalFeedbackStates: actionCreatorTypeGuard<
    QuestionState,
    Array<FeedbackState | null>
  >("generalFeedbackStates", "QuestionState"),
  nonzoneFeedbackStates: actionCreatorTypeGuard<
    QuestionState,
    Array<FeedbackState | null>
  >("nonzoneFeedbackStates", "QuestionState"),
  interactionType: actionCreatorTypeGuard<QuestionState, string>(
    "interactionType",
    "QuestionState"
  ),
  attempts: actionCreatorTypeGuard<QuestionState, number>(
    "attempts",
    "QuestionState"
  ),
  completed: actionCreatorTypeGuard<QuestionState, boolean>(
    "completed",
    "QuestionState"
  ),
  completed_timestamp: actionCreatorTypeGuard<
    QuestionState,
    FirestoreTimestampType | null
  >("completed_timestamp", "QuestionState"),
  __record: actionCreatorTypeGuard<TypeArray, QuestionState>(
    "QuestionState",
    "__record"
  ),
  __create: actionCreatorTypeGuard<TypeArray, null | Partial<QuestionState>>(
    "create",
    "__create/QuestionState"
  ),
  __delete: actionCreatorTypeGuard<TypeArray, number>("delete", "__record")
}

// Type safety to ensure we have a defined action for each property
type ReducerActions = {
  [key in
    | keyof QuestionState
    | "__record"
    | "__delete"
    | "__create"]: (typeof reducerAction)[key][0]
}

// Our exported actions
export const questionStateAction = Object.fromEntries(
  Object.entries(reducerAction).map(k => [k[0], k[1][0]])
) as ReducerActions

const validationOptions = {
  strict: true,
  abortEarly: false,
  stripUnknown: true,
  recursive: true
}

export const questionStateReducer = reducerTypeGuard<QuestionState>()
  .case(...reducerAction.answerStates)
  .case(...reducerAction.feedbackStates)
  .case(...reducerAction.nonzoneFeedbackStates)
  .case(...reducerAction.generalFeedbackStates)
  .case(...reducerAction.attempts)
  .case(questionStateAction.completed, (state, completed) => ({
    ...state,
    completed: completed,
    completed_timestamp: firebase.firestore.Timestamp.now()
  }))
  .case(...reducerAction.completed_timestamp)
  .default((state, action: any) => {
    let prevState = _.cloneDeep(state)
    action = processActionMeta(action)

    const {
      prop,
      idx,
      nodes
    }: { prop: keyof QuestionState; idx: number; nodes: Array<any> } =
      action?.meta
    if (
      [
        "answerStates",
        "feedbackStates",
        "generalFeedbackStates",
        "nonzoneFeedbackStates"
      ].includes(prop)
    ) {
      if (!prevState?.[prop]) {
        prevState = { ...prevState, [prop]: [] }
      }

      if (prop == "answerStates" && !prevState[prop]?.[idx]) {
        prevState[prop][idx] = AnswerStateSchema.cast({}, validationOptions)
      }
      if (prop == "feedbackStates" && !prevState[prop]?.[idx]) {
        prevState[prop][idx] = FeedbackStateSchema.cast({}, validationOptions)
      }
      if (prop == "generalFeedbackStates" && !prevState[prop]?.[idx]) {
        prevState[prop][idx] = FeedbackStateSchema.cast({}, validationOptions)
      }
      if (prop == "nonzoneFeedbackStates" && !prevState[prop]?.[idx]) {
        prevState[prop][idx] = FeedbackStateSchema.cast({}, validationOptions)
      }
    }

    switch (action?.meta?.prop) {
      case "answerStates":
        return getArrayTypeReducerGuard<QuestionState, AnswerState>(
          "QuestionState",
          "answerStates",
          "AnswerState",
          prevState,
          action
        )
      case "feedbackStates":
        return getArrayTypeReducerGuard<QuestionState, FeedbackState | null>(
          "QuestionState",
          "feedbackStates",
          "FeedbackState",
          prevState,
          action
        )
      case "generalFeedbackStates":
        return getArrayTypeReducerGuard<QuestionState, FeedbackState | null>(
          "QuestionState",
          "generalFeedbackStates",
          "FeedbackState",
          prevState,
          action
        )
      case "nonzoneFeedbackStates":
        return getArrayTypeReducerGuard<QuestionState, FeedbackState | null>(
          "QuestionState",
          "nonzoneFeedbackStates",
          "FeedbackState",
          prevState,
          action
        )
    }

    return prevState
  })
